/**
 * AdminRisk — /admin/risk
 * Market exposure table + sharp bettors list + KYC queue.
 */
import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  adminGetRiskPage,
  adminReviewKYC,
  type RiskPage,
  type KYCQueueItem,
} from '../../services/api';
import { formatZAR } from '../../utils/currency';

interface AdminCtx {
  adminEmail: string | null;
}

function KYCCard({
  item,
  email,
  onRefresh,
}: {
  item: KYCQueueItem;
  email: string;
  onRefresh: () => void;
}) {
  const [loading, setLoading] = useState(false);
  const [reason, setReason] = useState('');
  const [showReject, setShowReject] = useState(false);

  const doAction = async (action: 'approve' | 'reject') => {
    setLoading(true);
    try {
      await adminReviewKYC(email, item.submission_id, action, reason || undefined);
      onRefresh();
    } catch {
      // ignore
    } finally {
      setLoading(false);
      setShowReject(false);
    }
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <div className="flex items-start justify-between">
        <div>
          <p className="font-medium text-gray-800">{item.email}</p>
          <p className="text-xs text-gray-400">
            Tier requested: <strong>{item.tier_requested}</strong> · Doc:{' '}
            {item.document_type}
          </p>
          <p className="text-xs text-gray-400">
            Submitted: {new Date(item.submitted_at).toLocaleDateString('en-ZA')}
          </p>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <button
            disabled={loading}
            onClick={() => doAction('approve')}
            className="rounded-lg bg-green-100 px-3 py-1.5 text-xs font-medium text-green-700 hover:bg-green-200 disabled:opacity-60"
          >
            Approve
          </button>
          <button
            disabled={loading}
            onClick={() => setShowReject((v) => !v)}
            className="rounded-lg bg-red-100 px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-200 disabled:opacity-60"
          >
            Reject
          </button>
        </div>
      </div>
      {showReject && (
        <div className="mt-3 flex gap-2">
          <input
            type="text"
            placeholder="Reason (optional)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="flex-1 rounded border border-gray-300 px-2 py-1 text-sm"
          />
          <button
            onClick={() => doAction('reject')}
            disabled={loading}
            className="rounded-lg bg-red-600 px-3 py-1 text-xs font-medium text-white hover:bg-red-700 disabled:opacity-60"
          >
            Confirm
          </button>
          <button
            onClick={() => setShowReject(false)}
            className="text-xs text-gray-400"
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}

export default function AdminRisk() {
  const { adminEmail } = useOutletContext<AdminCtx>();
  const [data, setData] = useState<RiskPage | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = async (email: string) => {
    try {
      const res = await adminGetRiskPage(email);
      setData(res.data);
      setError(null);
    } catch {
      setError('Failed to load risk data.');
    }
  };

  useEffect(() => {
    if (adminEmail) load(adminEmail);
  }, [adminEmail]);

  return (
    <div>
      <h1 className="mb-6 text-2xl font-bold text-gray-900">Risk</h1>

      {error && (
        <div className="mb-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-600">{error}</div>
      )}

      {/* Market exposures */}
      <section className="mb-8">
        <h2 className="mb-3 text-lg font-semibold text-gray-800">Market Exposures</h2>
        <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
          <table className="min-w-full divide-y divide-gray-100">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Event</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Market</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Liability</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-40">Exposure</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {!data && (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-sm text-gray-400">
                    Loading...
                  </td>
                </tr>
              )}
              {data && data.market_exposures.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-sm text-gray-400">
                    No open exposures.
                  </td>
                </tr>
              )}
              {data?.market_exposures.map((m) => (
                <tr key={m.market_id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-gray-700">{m.event_name}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{m.market_name}</td>
                  <td className="px-4 py-3 text-right text-sm font-mono text-gray-800">
                    {formatZAR(m.open_liability_cents)}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-2 rounded-full bg-gray-200 overflow-hidden">
                        <div
                          className={`h-full rounded-full ${
                            m.exposure_pct >= 80
                              ? 'bg-red-500'
                              : m.exposure_pct >= 50
                              ? 'bg-yellow-400'
                              : 'bg-violet-500'
                          }`}
                          style={{ width: `${Math.min(m.exposure_pct, 100)}%` }}
                        />
                      </div>
                      <span className="text-xs text-gray-500 w-10 text-right">
                        {m.exposure_pct}%
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Sharp bettors */}
      <section className="mb-8">
        <h2 className="mb-3 text-lg font-semibold text-gray-800">Sharp Bettors</h2>
        {data?.sharp_bettors.length === 0 && (
          <p className="text-sm text-gray-400">No sharp bettors flagged.</p>
        )}
        <div className="space-y-2">
          {data?.sharp_bettors.map((b) => (
            <div
              key={b.user_id}
              className="flex items-center justify-between rounded-xl border border-gray-200 bg-white px-4 py-3"
            >
              <div>
                <p className="font-medium text-gray-800 text-sm">{b.email}</p>
                <p className="text-xs text-gray-400">
                  Win streak: <strong>{b.win_streak}</strong> · Total wagered:{' '}
                  {formatZAR(b.total_wagered_cents)}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* KYC queue */}
      <section>
        <h2 className="mb-3 text-lg font-semibold text-gray-800">KYC Queue</h2>
        {data?.kyc_queue.length === 0 && (
          <p className="text-sm text-gray-400">KYC queue is empty.</p>
        )}
        <div className="space-y-3">
          {data?.kyc_queue.map((item) =>
            adminEmail ? (
              <KYCCard
                key={item.submission_id}
                item={item}
                email={adminEmail}
                onRefresh={() => load(adminEmail)}
              />
            ) : null,
          )}
        </div>
      </section>
    </div>
  );
}
